import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {environment} from '../../environments/environment';
import {RecaptchaService} from './recaptcha.service';
import {AltchaSolver} from './altcha-solver.service';

/**
 * tenant-admin's `GET /tenant-signup/captcha/config` (Story 54.4): which
 * captcha the platform's signup form runs. brand-site has no discovery
 * document, so this is its runtime source. `none` = nothing is loaded for the
 * selected provider: the form posts without a token (the reCAPTCHA-unset
 * posture tenant signup has always had).
 */
export interface SignupCaptchaConfig {
  provider: string;
  site_key?: string;
  challenge_path?: string;
}

/** The selected provider's request field (this API is camelCase) and token. */
export interface SignupCaptchaToken {
  field: 'recaptchaToken' | 'altchaPayload';
  value: string;
}

const FLOW = 'tenant_signup';

/**
 * Story 54.4 — brand-site's facade over both captcha providers (a copy of
 * emjo-login's; the apps share no library). Everything is fetched AT SUBMIT:
 * the config (so a provider flip needs no reload) and, for ALTCHA, a fresh
 * single-use challenge — never cached, so a retry after a failed submit is
 * not a replay 403. Any failure rejects; the caller shows the captcha error
 * and does not submit.
 */
@Injectable({providedIn: 'root'})
export class CaptchaService {
  private readonly http = inject(HttpClient);
  private readonly recaptcha = inject(RecaptchaService);
  private readonly altcha = inject(AltchaSolver);

  /** Resolves null when the form must post without a token (`none`). */
  async solve(): Promise<SignupCaptchaToken | null> {
    const config = await this.fetchConfig();
    switch (config.provider) {
      case 'none':
        return null;
      case 'recaptcha':
        return {field: 'recaptchaToken', value: await this.recaptcha.execute(FLOW, config.site_key || environment.recaptchaSiteKey)};
      case 'altcha': {
        if (!config.challenge_path) {
          throw new Error('ALTCHA selected without a challenge path');
        }
        // The widget download runs alongside the challenge fetch (the TTL must
        // not run down during a cold load; the no-op catch only avoids an
        // unhandled rejection if the fetch fails first — solve() still throws).
        const loading = this.altcha.load();
        loading.catch(() => undefined);
        const challenge = await firstValueFrom(this.http.get<unknown>(this.onSignupHost(config.challenge_path)));
        await loading;
        return {field: 'altchaPayload', value: await this.altcha.solve(challenge)};
      }
    }
    throw new Error(`unsupported captcha provider: ${config.provider}`);
  }

  // A tenant-admin from before 54.4 has no config endpoint: reCAPTCHA with
  // the build-time key, exactly the pre-54.4 form (rollout safety). A 200 of
  // another shape (the route missing on a host, so another backend answered)
  // is the same case. An ALTCHA-selected deployment that cannot answer gets a
  // reCAPTCHA token, which it fails closed (403) — never a pass.
  private async fetchConfig(): Promise<SignupCaptchaConfig> {
    try {
      const config = await firstValueFrom(this.http.get<SignupCaptchaConfig>(`${environment.tenantSignupUrl}/captcha/config`));
      return typeof config?.provider === 'string' ? config : {provider: 'recaptcha'};
    } catch {
      return {provider: 'recaptcha'};
    }
  }

  // challenge_path is absolute on tenant-admin's host, which is the host of
  // tenantSignupUrl (relative in-cluster, absolute in the legacy envs).
  private onSignupHost(path: string): string {
    return new URL(path, new URL(environment.tenantSignupUrl, window.location.href)).toString();
  }
}
